import axios from '@fs/zion-axios'
import { i18n } from '@fs/zion-locale'

const hertitageSupportedTileIdMap = {
  'PRICE:STAMP_PRICE': 'stampprice',
  'PRICE:MOVIE_PRICE': 'movieticketprice',
  'TEMPLE:TEMPLE_DEDICATION': '',
  'FILM:BEST_FILM': 'bestfilm',
  'FILM:TOP_FILM': 'topfilm',
  'MUSIC:ALBUM': 'album',
  'MUSIC:POP_SONG': 'popsong',
  'MUSIC:SONG_OF_YEAR': 'songofyear',
  'SPORTS:TENNIS': 'tennischamps',
  'PRICE:GAS_PRICE': 'gasprice',
  COUNTRY_POPULATION: 'worldpopulation',
}

const serviceBase = '/service/discovery/allaboutme'

export const getTileData = async ({ countryCode, year }) => {
  const headers = {
    'Accept-Language': i18n.language,
  }
  const tileServiceEndpoint = `${serviceBase}/tile/available/region/${countryCode}/year/${year}`
  return axios.get(tileServiceEndpoint, { headers })
}

export const postBirthDate = async ({ personId, birthDate }) => {
  return axios.post(`/service/discovery/common/tree/birthdate/${personId}?birthDate=${birthDate}`)
}

export const getVariantData = async ({ nameArray, firstName }) => {
  const createFirstNameUrl = (variantName) => `/service/homelands/core/name?text=${variantName}&type=FIRST&format=html`
  const createLastNameUrl = (variantName) => `/service/homelands/core/name?text=${variantName}&type=LAST&format=html`

  if (firstName) {
    return axios.all(nameArray.map((vari) => axios.get(createFirstNameUrl(vari.name))))
  }

  return axios.all(nameArray.map((vari) => axios.get(createLastNameUrl(vari.name))))
}

export const formatTileContentData = (tileData) => {
  const isFirst = tileData?.type !== 'globalNameCounts' // tile id changed at some point. Set isFirst if not 'globalNameCounts' instead
  const firstName = tileData?.parsedNames?.firstName?.name ?? ''
  const middleNames = tileData?.parsedNames?.middleNames
    ? `${tileData?.parsedNames?.middleNames?.map((obj) => obj.name).join(' ')} `
    : ''
  const lastName = tileData?.parsedNames?.lastName?.name ?? ''
  return {
    type: tileData?.type,
    supportedTileId: hertitageSupportedTileIdMap[tileData?.supportedTileId] || tileData?.supportedTileId,
    homelandId: tileData?.homelandId,
    year: tileData?.year,
    countryCode: tileData?.region,
    tileId: tileData?.id,
    nameArgs: { name: isFirst ? firstName : (lastName ?? middleNames), isFirst },
    dataUrl: tileData.dataUrlTemplate ? tileData.dataUrlTemplate.replace('{year}', tileData?.year) : tileData?.dataUrl,
  }
}

export const getTileContentData = async ({
  supportedTileId,
  type,
  countryCode,
  nameArgs: { name, isFirst },
  dataUrl,
}) => {
  const firstNameHeatmapURL = `${serviceBase}/tile/name/count/first/name/${name}/region/${countryCode}`
  const lastNameHeatmapURL = `${serviceBase}/tile/name/count/last/name/${name}/region/${countryCode}`
  const generalTileDataURL = dataUrl
  const surnameCountURL = `${serviceBase}/treesurnamecount/${name}`
  const firstNameMeaningURL = `/service/homelands/core/name?text=${name}&type=FIRST&format=html`
  const lastNameMeaningURL = `/service/homelands/core/name?text=${name}&type=LAST&format=html`
  let urlToUse = ''
  if (supportedTileId === 'namemeaning' || type === 'nameMeaning') {
    urlToUse = isFirst ? firstNameMeaningURL : lastNameMeaningURL
  } else if (supportedTileId === 'nameheatmap' || type === 'nameHeatmap') {
    urlToUse = isFirst ? firstNameHeatmapURL : lastNameHeatmapURL
  } else if ((supportedTileId === 'myheritage' && name) || type === 'globalNameCounts') {
    urlToUse = surnameCountURL
  } else if (type === 'quiz') {
    const endpoint = dataUrl
    return axios
      .get(endpoint)
      .then((res) => {
        function formatAnswers({ answer, choices }) {
          const answerIndex = choices.indexOf(answer)
          choices.splice(answerIndex, 1)
          choices.unshift(answer)
          return choices.join('|')
        }
        res.data = [
          {
            id: res.data.quiz.id,
            year: res.data.year,
            text: res.data.quiz.question,
            quizType: '',
            answers: formatAnswers(res.data),
            answerText: res.data.answer,
            text1: res.data.body[0],
            text2: res.data.body[1],
            text3: res.data.body[2],
            text4: res.data.body[3],
            language: res.data.quiz.language,
            region: res.data.quiz.region,
          },
        ]
        return res
      })
      .catch((err) => {
        return { data: err }
      })
  } else if (type === 'fact' || type === 'event') {
    const endpoint = dataUrl
    return axios.get(endpoint).then((res) => {
      if (res.data.items) {
        res.data = res.data.items
          .map((item) => ({
            id: item.id,
            year: item.startYear,
            startYear: item.startYear,
            endYear: item.endYear,
            tileType: item.type,
            localeOnly: item.region,
            languageOnly: item.availableLanguages,
            categoryId: 0,
            icon: '',
            textTitle: item.title,
            textHeadline: item.body[0],
            textAdditional: item.body[1],
            category: item.category,
            subcategory: item.subcategory,
          }))
          .filter(
            (item) =>
              item.localeOnly?.toLowerCase() === countryCode.toLowerCase() ||
              item.localeOnly?.toLowerCase() === 'global'
          )
      } else res.data = undefined
      return res
    })
  } else if (type === 'lineChart') {
    const endpoint = dataUrl
    return axios.get(endpoint).then((res) => {
      res.data = res.data.items
        .map((item) => {
          return {
            id: item.id,
            year: item.startYear,
            price:
              item.numericValue || item.numericalValue || item?.body ? parseFloat(item.body[0].replace('$', '')) : 0,
            tileType: item.type,
            localeOnly: item.region,
            languageOnly: item.language,
            categoryId: 0,
            icon: '',
            textTitle: item.title,
            textHeadline: item.body ? item.body[0] : '',
            category: item.category,
            subcategory: item.subcategory,
          }
        })
        .filter((item) => item.localeOnly?.toLowerCase() === countryCode.toLowerCase())
      return res
    })
  } else {
    urlToUse = generalTileDataURL
  }
  return axios.get(urlToUse)
}
